import React from 'react';
import { Link } from 'react-router-dom';
import { Mail, Phone, MapPin, AlertCircle, Shield, CheckCircle, Award, Lock } from 'lucide-react';
import NewsletterSignup from './NewsletterSignup';

const Footer = () => {
  return (
    <footer className="bg-gray-900 text-gray-400">
      {/* Vertrauenssymbole Leiste */}
      <div className="bg-gray-800 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-4">
          <div className="flex flex-wrap justify-center gap-6 md:gap-12">
            <div className="flex items-center gap-2">
              <Lock className="w-5 h-5 text-green-500" />
              <span className="text-sm text-gray-300">SSL-verschlüsselt</span>
            </div>
            <div className="flex items-center gap-2">
              <Shield className="w-5 h-5 text-green-500" />
              <span className="text-sm text-gray-300">DSGVO-konform</span>
            </div>
            <div className="flex items-center gap-2">
              <CheckCircle className="w-5 h-5 text-green-500" />
              <span className="text-sm text-gray-300">Geprüfte Qualität</span>
            </div>
            <div className="flex items-center gap-2">
              <Award className="w-5 h-5 text-green-500" />
              <span className="text-sm text-gray-300">20+ Jahre Erfahrung</span>
            </div>
          </div>
        </div>
      </div>

      {/* Disclaimer Section */}
      <div className="bg-gray-850 border-b border-gray-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-6">
          <div className="flex items-start space-x-3">
            <AlertCircle className="w-6 h-6 text-yellow-400 flex-shrink-0 mt-0.5" />
            <div>
              <h3 className="text-white font-semibold mb-2">Hinweis:</h3>
              <div className="text-sm text-gray-400 leading-relaxed space-y-1">
                <p>Wir bieten keine Rechts- oder Steuerberatung an und verkaufen keine Gesellschaften.</p>
                <p>Die Plattform dient der Vermittlung und organisatorischen Begleitung von GmbH-Gründungen, Vorratsgesellschaften sowie Gesellschafterübertragungen.</p>
                <p>Sämtliche gesellschaftsrechtlichen Vorgänge erfolgen ausschließlich durch Notare und externe, eigenverantwortliche Partner.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="grid grid-cols-1 md:grid-cols-4 gap-8">
          {/* Company Info */}
          <div className="space-y-4">
            <img 
              src="/firmbay-logo-optimized.png" 
              alt="Firmbay Logo" 
              width="240"
              height="160"
              className="h-32 md:h-40 w-auto"
            />
            <p className="text-sm text-gray-400">
              Dein Partner für professionelle GmbH-Gründungen und Unternehmensberatung in Deutschland.
            </p>
          </div>

          {/* Quick Links */}
          <div>
            <h3 className="text-white font-semibold mb-4">Schnellzugriff</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/gmbh-gruenden" className="text-sm hover:text-blue-400 transition-colors">
                  GmbH gründen - Infos
                </Link>
              </li>
              <li>
                <Link to="/marktplatz" className="text-sm hover:text-blue-400 transition-colors">
                  GmbH kaufen
                </Link>
              </li>
              <li>
                <Link to="/seller-onboarding" className="text-sm hover:text-blue-400 transition-colors">
                  GmbH verkaufen
                </Link>
              </li>
              <li>
                <Link to="/onboarding" className="text-sm hover:text-blue-400 transition-colors">
                  Gründungsformular
                </Link>
              </li>
              <li>
                <Link to="/ueber-uns" className="text-sm hover:text-blue-400 transition-colors">
                  Über uns
                </Link>
              </li>
              <li>
                <Link to="/faq" className="text-sm hover:text-blue-400 transition-colors">
                  Häufige Fragen (FAQ)
                </Link>
              </li>
              <li>
                <Link to="/blog" className="text-sm hover:text-blue-400 transition-colors">
                  Blog & Ratgeber
                </Link>
              </li>
            </ul>
          </div>

          {/* Legal */}
          <div>
            <h3 className="text-white font-semibold mb-4">Rechtliches</h3>
            <ul className="space-y-2">
              <li>
                <Link to="/rechtliches" className="text-sm hover:text-blue-400 transition-colors">
                  Impressum
                </Link>
              </li>
              <li>
                <Link to="/rechtliches#datenschutz" className="text-sm hover:text-blue-400 transition-colors">
                  Datenschutz
                </Link>
              </li>
              <li>
                <Link to="/rechtliches#agb" className="text-sm hover:text-blue-400 transition-colors">
                  AGB
                </Link>
              </li>
            </ul>
          </div>

          {/* Contact */}
          <div>
            <h3 className="text-white font-semibold mb-4">Kontakt</h3>
            <ul className="space-y-3">
              <li className="flex items-start space-x-3">
                <Mail className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <span className="text-sm">info@firmbay.de</span>
              </li>
              <li className="flex items-start space-x-3">
                <Phone className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <span className="text-sm">0170-7967959</span>
              </li>
              <li className="flex items-start space-x-3">
                <MapPin className="w-5 h-5 text-blue-400 mt-0.5 flex-shrink-0" />
                <span className="text-sm">Kontor H72, Hansastr. 72<br />44137 Dortmund</span>
              </li>
            </ul>
          </div>
        </div>

        {/* Newsletter Section */}
        <div className="border-t border-gray-800 mt-8 pt-8">
          <div className="max-w-md">
            <h3 className="text-white font-semibold mb-4">Newsletter</h3>
            <p className="text-sm text-gray-400 mb-4">
              Erhalte aktuelle GmbH-Angebote und Gründungstipps direkt in dein Postfach.
            </p>
            <NewsletterSignup variant="footer" />
          </div>
        </div>

        <div className="border-t border-gray-800 mt-12 pt-8 text-center text-sm text-gray-400">
          <p>&copy; {new Date().getFullYear()} Firmbay. Alle Rechte vorbehalten.</p>
        </div>
      </div>
    </footer>
  );
};

export default Footer;